import type React from 'react';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import Badge from '@zg-rentals/multibrand-ui/lib/Badge';
import { formatDate, printDaysBetweenDates } from '@common/paymentsUtils/dateUtils';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import { capitalizeFirstLetter } from '@common/utils/stringUtils';
import TransactionConstants from '@common/constants/TransactionConstants';
import ONE_TIME_PAYMENT_TYPE from '@common/constants/oneTimePaymentTypes';
import DepositDateTooltipComponent from '@rental-manager/components/StyledTooltip/DepositDateToolTip';
import type { ITransactionModel } from '@rental-manager/models/Types';
import { getFirstName } from '@rental-manager/models/PropertyInfo';

const getStatusText = (status: string, dateReceived: Date | string): React.ReactNode => {
  switch (status) {
    case TransactionConstants.STATUS.PENDING:
      return (
        <Text type="body" className="fs-exclude">
          Deposit date: {formatDate(dateReceived, 'M/d/yy')}
          <DepositDateTooltipComponent />
        </Text>
      );
    case TransactionConstants.STATUS.COMPLETE:
      return <Text className="fs-exclude">Deposited: {formatDate(dateReceived, 'M/d/yy')}</Text>;
    case TransactionConstants.STATUS.REFUNDED:
    case TransactionConstants.STATUS.DISPUTED:
    case TransactionConstants.STATUS.PAYMENT_FAILED:
    case TransactionConstants.STATUS.DEPOSIT_FAILED:
    case TransactionConstants.STATUS.UNPAID:
    case TransactionConstants.STATUS.PAST_DUE:
    default:
      return <></>;
  }
};

const getPaymentSent = (status: string, dateCreated: Date | string): React.ReactNode => {
  if (status === TransactionConstants.STATUS.PENDING || status === TransactionConstants.STATUS.COMPLETE) {
    return <Text className="fs-exclude">Payment sent: {formatDate(dateCreated, 'M/d/yy')}</Text>;
  }
  return <></>;
};

const getPaymentTypeLabel = (paymentType: string, paymentPlanInstallmentTitle = ''): string => {
  switch (paymentType) {
    case ONE_TIME_PAYMENT_TYPE.OTHER:
      return `${capitalizeFirstLetter(paymentType)} payment`;
    case ONE_TIME_PAYMENT_TYPE.LATE_FEE:
      return 'Late fee';
    case ONE_TIME_PAYMENT_TYPE.PET_FEE:
      return 'Pet fee';
    case ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN:
      return paymentPlanInstallmentTitle;
    default:
      return capitalizeFirstLetter(paymentType);
  }
};

const getBankType = (category: string, accountType: string): string => {
  if (category === 'upcoming') return '';
  switch (accountType) {
    case 'credit':
      return 'Credit card';
    case 'debit':
      return 'Debit card';
    case 'bank_account':
      return 'ACH transfer';
    default:
      return '';
  }
};

type Props = {
  transaction: ITransactionModel;
  cancelOneTimePayment: ({
    otpKey,
    tenantFirstName,
    paymentType,
    paymentPlanInstallmentTitle,
  }: Record<string, string>) => void;
  today: Date | string;
  remindTenantToPayOneTimePayment: ({ tenantEmail, requestKey }) => void;
};

const OneTimePaymentsRow: React.FC<React.PropsWithChildren<Props>> = (props: Props) => {
  const { transaction, cancelOneTimePayment, today = new Date() } = props;
  const {
    groupKey,
    requestKey,
    paymentCategory,
    tenant,
    accountType,
    landlordStatus,
    paymentType,
    notes,
    dateRequested,
    dateCreated,
    dateReceived,
    amount,
    paymentPlanInstallmentTitle = '',
  } = transaction;
  const tenantFirstName = getFirstName(tenant.name);
  const paymentTypeLabel = getPaymentTypeLabel(paymentType, paymentPlanInstallmentTitle);
  const dateRequestedLocalDate = dateRequested;
  const daysBetweenDates = printDaysBetweenDates(today, dateRequestedLocalDate);
  const otpKey = transaction.paymentType === ONE_TIME_PAYMENT_TYPE.PAYMENT_PLAN ? requestKey : groupKey;
  const cancelOneTimePaymentRequest = () =>
    cancelOneTimePayment({ otpKey, tenantFirstName, paymentType, paymentPlanInstallmentTitle });

  const remindTenantToPayOneTimePayment = (): void => {
    const tenantEmail = tenant.email;
    return props.remindTenantToPayOneTimePayment({ tenantEmail, requestKey });
  };
  return (
    <Row>
      <Col xs={0} sm={0} md={10} lg={10} xl={10} xxl={10}>
        <Row>
          <Col>
            <Text className="fs-exclude" emphasize>
              {paymentTypeLabel}
            </Text>
            {notes && <Text>{notes}</Text>}
            {getStatusText(landlordStatus, dateReceived)}
            <Text className="fs-exclude">{tenant.name}</Text>
            {paymentCategory === 'completed' && (
              <Text className="fs-exclude">{getBankType(paymentCategory, accountType)}</Text>
            )}
            {landlordStatus === TransactionConstants.STATUS.UNPAID && (
              <Text>
                <Link onClick={cancelOneTimePaymentRequest}>Cancel request</Link>
                <span className="inset-xs">|</span>
                <Link onClick={remindTenantToPayOneTimePayment}>Remind the tenant</Link>
              </Text>
            )}
            {paymentCategory === 'upcoming' && (
              <Text className="fs-exclude" lowlight>
                Requested {daysBetweenDates}
              </Text>
            )}
            {getPaymentSent(landlordStatus, dateCreated)}
          </Col>
        </Row>
      </Col>
      <Col xs={0} sm={0} md={2} lg={2} xl={2} xxl={2}>
        <Row>
          <Col className="Dashboard-Price-Badge-Padding txtR">
            <Text type="body" className="Dashboard-Price fs-exclude">
              {centsToDollars(amount)}
            </Text>
            {landlordStatus === TransactionConstants.STATUS.PENDING && (
              <Badge className="txtR fs-exclude" label="pending" type="info" />
            )}
          </Col>
        </Row>
      </Col>
      <Col xs={8} sm={8} md={0} lg={0} xl={0} xxl={0} className="paymentDetailsRow-Mobile">
        <Row>
          <Col>
            <Text className="fs-exclude" emphasize>
              {paymentTypeLabel}
            </Text>
            {notes && <Text>{notes}</Text>}
            {getStatusText(landlordStatus, dateReceived)}
            <Text className="fs-exclude">{tenant.name}</Text>
            {paymentCategory === 'completed' && (
              <Text className="fs-exclude">{getBankType(paymentCategory, accountType)}</Text>
            )}
            {landlordStatus === TransactionConstants.STATUS.UNPAID && (
              <Link onClick={cancelOneTimePaymentRequest}>
                <Text>Cancel payment</Text>
              </Link>
            )}
            {paymentCategory === 'upcoming' && <Text lowlight>Requested {daysBetweenDates}</Text>}
            {getPaymentSent(landlordStatus, dateCreated)}
          </Col>
        </Row>
      </Col>
      <Col xs={4} sm={4} md={0} lg={0} xl={0} xxl={0} className="paymentDetailsRow-Mobile">
        <Col xs={12} sm={12} md={0} lg={0} xl={0} xxl={0}>
          <Row>
            <Col className="Dashboard-Price-Badge-Padding txtR">
              <Text type="body" className="Dashboard-Price fs-exclude">
                {centsToDollars(amount)}
              </Text>
              {landlordStatus === TransactionConstants.STATUS.PENDING && (
                <Badge className="txtR fs-exclude" label="pending" type="info" />
              )}
            </Col>
          </Row>
        </Col>
      </Col>
    </Row>
  );
};
export default OneTimePaymentsRow;
